import { useEffect, useMemo, useRef, useState } from 'react';
import { render } from '@markbird/markdown';
import { useStore } from '../store/useStore';
import { useT } from '../i18n/useT';
import { listSnapshots, getSnapshot, captureSnapshot } from '../lib/snapshots';
import { saveDocument } from '../lib/library';
import { resolveImagesInDom } from '../lib/images';
import { renderKatex } from '../lib/katex';
import { renderMermaid } from '../lib/mermaid';
import type { DocSnapshot } from '../lib/db';
import { Modal } from './Modal';
import { formatDateTime } from '../lib/format';

/**
 * Version history for the active document. Lists local snapshots (periodic
 * autosaves + points captured before a remote overwrite or a restore), previews
 * the selected version, and restores it — capturing the current state first so
 * a restore is itself undoable.
 */
export function HistoryDialog() {
  const t = useT();
  const open = useStore((s) => s.historyOpen);
  const setOpen = useStore((s) => s.setHistoryOpen);
  const activeId = useStore((s) => s.activeDoc.id);
  const [snaps, setSnaps] = useState<DocSnapshot[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open || !activeId) return;
    void listSnapshots(activeId).then((list) => {
      setSnaps(list);
      setSelected(list[0]?.id ?? null);
    });
  }, [open, activeId]);

  const current = useMemo(() => snaps.find((s) => s.id === selected) ?? null, [snaps, selected]);
  const html = useMemo(() => (current ? render(current.content) : ''), [current]);

  useEffect(() => {
    if (bodyRef.current && html) {
      bodyRef.current.innerHTML = html;
      void renderMermaid(bodyRef.current);
      void renderKatex(bodyRef.current);
      void resolveImagesInDom(bodyRef.current);
    }
  }, [html]);

  if (!open) return null;

  const saveNow = async () => {
    if (!activeId) return;
    setBusy(true);
    try {
      // captureSnapshot skips a copy identical to the latest one, atomically.
      await captureSnapshot(useStore.getState().activeDoc, 'auto');
      const list = await listSnapshots(activeId);
      setSnaps(list);
      setSelected(list[0]?.id ?? null);
    } finally {
      setBusy(false);
    }
  };

  const restore = async () => {
    if (!selected || !activeId) return;
    setBusy(true);
    try {
      const snap = await getSnapshot(selected);
      if (!snap) return;
      const cur = useStore.getState().activeDoc;
      // Snapshot the current state first so the restore can be undone.
      if (cur.content !== snap.content) await captureSnapshot(cur, 'restore');
      await saveDocument(activeId, { title: snap.title, content: snap.content });
      useStore.getState().reloadOpenDoc?.(activeId);
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const fmt = (ms: number) => formatDateTime(ms);

  return (
    <Modal
      onClose={() => setOpen(false)}
      title={t('history.title')}
      className="mb-modal-wide mb-history"
    >
      {snaps.length === 0 ? (
        <p className="mb-modal-note">{t('history.empty')}</p>
      ) : (
        <div className="mb-history-body">
          <ul className="mb-history-list">
            {snaps.map((s) => (
              <li key={s.id}>
                <button
                  className={`mb-history-item ${s.id === selected ? 'active' : ''}`}
                  onClick={() => setSelected(s.id)}
                >
                  <span className="mb-history-time">{fmt(s.createdAt)}</span>
                  <span className="mb-history-reason">{t(`history.reason.${s.reason}`)}</span>
                </button>
              </li>
            ))}
          </ul>
          <div className="mb-history-preview markbird-preview markdown-preview" ref={bodyRef} />
        </div>
      )}

      <div className="mb-modal-actions">
        <span style={{ flex: 1 }} />
        <button className="mb-btn" disabled={busy} onClick={() => void saveNow()}>
          {t('history.saveNow')}
        </button>
        {current && (
          <button className="mb-btn mb-btn-primary" disabled={busy} onClick={() => void restore()}>
            {busy ? t('history.restoring') : t('history.restore')}
          </button>
        )}
      </div>
    </Modal>
  );
}
